import React, {useState, useMemo, useEffect} from 'react';
import {Box, InputAdornment, Typography} from '@mui/material';
import TextField from '@jobseeker/lotus/elements/Input';
import Dialog from '@jobseeker/lotus/components/Dialog';
import {VersionVariants} from '@jobseeker/lotus/enums/variants';
import Image from 'next/image';
import {SearchIcon} from '@jobseeker/lotus/icons';

const isAppIimjobs = process.env.NEXT_PUBLIC_APP === 'iimjobs';

interface FunctionalArea {
  id: number;
  name: string;
  cluster?: string;
  category?: string;
}

interface FunctionalAreaDialogV2Props {
  open: boolean;
  onClose: () => void;
  title: string;
  functionalAreasData: any;
  onChange: any;
  subtitle?: string;
  label?: string;
  className?: string;
  selectedValue?: number;
}

const FunctionalAreaDialogV2: React.FC<FunctionalAreaDialogV2Props> = ({
  open,
  onClose,
  title,
  functionalAreasData,
  onChange,
  subtitle,
  label = 'Search Functional Area',
  className = '',
  selectedValue,
}) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState<string | null>(null);

  const {fullData, listData} = useMemo(() => {
    if (isAppIimjobs) {
      return {
        fullData: functionalAreasData || [],
        listData: functionalAreasData || [],
      };
    }

    const fd = functionalAreasData || [];

    return {
      fullData: fd,
      listData: fd.filter((x: any) => x.category !== ''),
    };
  }, [functionalAreasData]);

  const normalizedFullData = useMemo(() => {
    const normalized: Record<string, FunctionalArea[]> = {};

    fullData.forEach((area: any) => {
      const cluster = area.cluster || 'Other';
      if (!normalized[cluster]) normalized[cluster] = [];
      normalized[cluster].push(area);
    });

    return normalized;
  }, [fullData]);

  const normalizedListData = useMemo(() => {
    const normalized: Record<string, FunctionalArea[]> = {};

    listData.forEach((area: any) => {
      const cluster = area.cluster || 'Other';
      if (!normalized[cluster]) normalized[cluster] = [];
      normalized[cluster].push(area);
    });

    return normalized;
  }, [listData]);

  const allFunctionalAreas = useMemo(() => {
    const merged: FunctionalArea[] = [];
    Object.values(normalizedFullData).forEach(areas => merged.push(...areas));
    return merged;
  }, [normalizedFullData]);

  const categories = Object.keys(normalizedListData);

  const selectedAreaCategory = useMemo(() => {
    if (!selectedValue) return null;

    for (const [category, areas] of Object.entries(normalizedFullData)) {
      if (areas.some(area => area.id === selectedValue)) return category;
    }

    return null;
  }, [selectedValue, normalizedFullData]);

  useEffect(() => {
    if (open && selectedValue && selectedAreaCategory) {
      setSelectedCategory(selectedAreaCategory);
    } else if (open && !selectedValue) {
      setSelectedCategory(null);
    }
  }, [open, selectedValue, selectedAreaCategory]);

  const currentAreas = selectedCategory
    ? normalizedListData[selectedCategory] || []
    : listData;

  const filteredAreas = searchQuery.trim()
    ? allFunctionalAreas.filter(area =>
        area.name.toLowerCase().includes(searchQuery.toLowerCase())
      )
    : currentAreas;

  const handleCategoryClick = (category: string) => {
    setSelectedCategory(selectedCategory === category ? null : category);
    setSearchQuery('');
  };

  const handleAreaSelect = (areaId: number) => {
    onChange(areaId);
    onClose();
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      title={title}
      invertedTitle={false}
      variant={VersionVariants.VERSION_SEVEN}
      subtitle={subtitle}
      className={className}
      paperMaxWidth={'562px'}
    >
      <Box
        sx={{
          height: '620px',
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        <Box sx={{p: 2, borderBottom: '1px solid #EEEEEE'}}>
          <TextField
            fullWidth
            placeholder={label}
            value={searchQuery}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Image src={SearchIcon} alt="search" width={16} height={16} />
                </InputAdornment>
              ),
            }}
            sx={{
              '.MuiInputBase-input': {
                border: 'unset !important',
                fontSize: '12px !important',
                paddingLeft: 0,
                py: '8px',
              },
              '.MuiInputBase-input::placeholder': {
                fontSize: '12px',
                fontWeight: 400,
                color: '#7E7E7E',
                opacity: 0.8,
              },
            }}
            onChange={e => setSearchQuery(e.target.value)}
            onKeyDown={e => {
              if (e.key !== 'Escape') e.stopPropagation();
            }}
            variant={VersionVariants.VERSION_TWO}
          />
        </Box>

        <Box sx={{display: 'flex', flex: 1, overflow: 'hidden'}}>
          <Box
            sx={{
              width: {xxs: '167px', md: '226px'},
              borderRight: '1px solid #EEEEEE',
              overflowY: 'auto',
              backgroundColor: '#FAFAFA',
            }}
          >
            {categories.map(category => (
              <Box
                key={category}
                onClick={() => handleCategoryClick(category)}
                sx={{
                  padding: '21px 16px',
                  cursor: 'pointer',
                  borderBottom: '1px solid #EEEEEE',
                  '&:last-child': {
                    borderBottom: 'none',
                  },
                }}
              >
                <Typography
                  sx={{
                    fontSize: '15px',
                    fontWeight: selectedCategory === category ? 500 : 400,
                    color:
                      selectedCategory === category ? '#333333' : '#7E7E7E',
                    textTransform: 'capitalize',
                  }}
                >
                  {category}
                </Typography>
              </Box>
            ))}
          </Box>

          <Box sx={{flex: 1, overflowY: 'auto', p: 1}}>
            {filteredAreas.length > 0 ? (
              filteredAreas.slice(0, 100).map((area: any) => (
                <Box
                  key={area.id}
                  onClick={() => handleAreaSelect(area.id)}
                  sx={{
                    padding: '7px 12px',
                    cursor: 'pointer',
                    borderRadius: '4px',
                    transition: 'background 0.2s',
                    backgroundColor:
                      selectedValue === area.id
                        ? isAppIimjobs
                          ? 'background.secondary'
                          : 'primary.light'
                        : 'transparent',
                    '&:hover': {
                      backgroundColor: isAppIimjobs
                        ? 'background.secondary'
                        : 'primary.light',
                    },
                  }}
                >
                  <Typography
                    sx={{fontSize: '15px', fontWeight: 400, color: '#333'}}
                  >
                    {area.name}
                  </Typography>
                </Box>
              ))
            ) : (
              <Box sx={{p: 2, textAlign: 'center'}}>
                <Typography
                  sx={{color: '#7E7E7E', fontSize: '14px', fontWeight: 400}}
                >
                  No results found
                </Typography>
              </Box>
            )}
          </Box>
        </Box>
      </Box>
    </Dialog>
  );
};

export default FunctionalAreaDialogV2;
